<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("admin.manage_organizers.add_organizer") }}</template>
        <template v-slot:body>
            <div class="field" :class="{ 'has-error': !validEmail }">
                <label class="label">{{ translate("common.email") }}:</label>
                <div class="control">
                    <input class="input" v-model="email" type="email" maxlength="50" />
                    <p class="help" v-if="!validEmail">
                        {{ translate("flash_messages.user_email_valid") }}
                    </p>
                </div>
            </div>
            <div class="field">
                <label class="label">{{ translate("admin.manage_organizers.role") }}:</label>
                <div class="control">
                    <div class="select">
                        <select v-model="role">
                            <option :value="EVENT_USER_ROLE_FULL_ORGANIZER">
                                {{ translate("admin.manage_organizers.organizer_role") }}
                            </option>
                            <option :value="EVENT_USER_ROLE_TECHNICIAN">
                                {{ translate("admin.manage_organizers.technician_role") }}
                            </option>
                            <option :value="EVENT_USER_ROLE_STAFF">
                                {{ translate("admin.manage_organizers.staff_role") }}
                            </option>
                        </select>
                    </div>
                    <template v-if="role === EVENT_USER_ROLE_STAFF">
                        <StaffBundlePicker v-model="bundles" />
                        <p
                            class="help"
                            v-if="federationId && canManageFederation"
                            v-html="
                                translate('admin.manage_organizers.staff_hint_federation_link', false, [
                                    urls.federations().federationMembersPage(federationId),
                                ])
                            "
                        ></p>
                        <p class="help" v-else>{{ translate("admin.manage_organizers.staff_hint_event_only") }}</p>
                    </template>
                </div>
            </div>

            <RoleDifferences scope="event" />
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton :is-loading="addingInProgress" class="button is-primary" @click="addOrganizer" :disabled="!canSubmit">
                    {{ translate("common.ok") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import { useModal } from "../Common/useModal";
    import { isValidEmailAndNotEmpty, isValidEmailOrEmpty } from "../Common/email";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import LoadingButton from "../Common/LoadingButton.vue";
    import RoleDifferences from "../Common/RoleDifferences.vue";
    import urls from "../Urls/urls";
    import StaffBundlePicker from "../Common/StaffBundlePicker.vue";
    import { EVENT_USER_ROLE_FULL_ORGANIZER, EVENT_USER_ROLE_TECHNICIAN, EVENT_USER_ROLE_STAFF, Bundle } from "../Common/consts";
    import { useOrganizersStore } from "./organizersStore";
    import manageEventOrganizersApi from "../Api/manageEventOrganizers";

    const props = defineProps<{ eventId: number; federationId?: string; canManageFederation?: boolean }>();

    const organizersStore = useOrganizersStore();

    const { modal, open: openModal, close } = useModal();

    const email = ref("");
    const role = ref(EVENT_USER_ROLE_STAFF);
    const bundles = ref<Bundle[]>([]);
    const addingInProgress = ref(false);

    const validEmail = computed(() => isValidEmailOrEmpty(email.value));
    const canSubmit = computed(
        () => isValidEmailAndNotEmpty(email.value) && (role.value !== EVENT_USER_ROLE_STAFF || bundles.value.length > 0),
    );

    function open(): void {
        email.value = "";
        role.value = EVENT_USER_ROLE_STAFF;
        bundles.value = [];
        openModal();
    }

    function addOrganizer() {
        addingInProgress.value = true;
        return manageEventOrganizersApi
            .addOrganizer(props.eventId, email.value, role.value, bundles.value)
            .then(() => {
                successToast(translate("admin.manage_organizers.organizer_added_successfully"));
                close();

                return organizersStore.refreshOrganizersListing();
            })
            .catch(handleError)
            .finally(() => {
                addingInProgress.value = false;
            });
    }

    defineExpose({ open });
</script>

<style scoped></style>
